/**
 * Global axios interceptor that auto-attaches a Bearer token to
 * event-mutation requests so the backend can authenticate the caller.
 *
 * Order of preference:
 *   1. Venue Admin JWT  (sessionStorage `venueAdminToken_${slug}`)
 *   2. Organizer JWT    (sessionStorage `invyt_organizer_token`)
 *
 * Only attaches to API paths that mutate events, plus venue Creator-Hub
 * profile/gallery mutations:
 *   - /api/events           (POST)
 *   - /api/events/{id}      (PUT, DELETE)
 *   - /api/events/{id}/hide / unhide
 *   - /api/events/series/...
 *   - /api/events/upload-hero
 *   - /api/venues/{id}/profile, /upload-cover, /media, /media/signed-upload,
 *     /media/{id}, /media/reorder
 *   - /api/venues/{id}/settings, /upload-logo, /staff (+ /staff/{id}) mutations
 *   - GET /api/venues/{id}/staff (admin staff list — returns PINs, must auth)
 *
 * Read endpoints (GET) and other API paths are untouched — except the admin
 * staff list above (the public kiosk uses /staff/list which stays open).
 */
import axios from "axios";
import { toast } from "sonner";

const MUTATING_METHODS = new Set(["post", "put", "delete", "patch"]);
const EVENT_API_RE = /\/api\/(events(\b|\/|$)|ticketing\/(events\/[^/]+\/tiers|tiers\/))/;
// Venue admin-owned mutations (Creator Hub + settings/logo/staff/team CRUD).
const VENUE_ADMIN_API_RE = /\/api\/venues\/[^/]+\/(profile|upload-cover|upload-logo|settings|media|staff|collaborators|billing)(\/|\b|$)/;
// Admin GETs that require the venue token: staff list (leaks PINs), team list,
// activity log and billing status.
const VENUE_ADMIN_READ_RE = /\/api\/venues\/[^/]+\/(staff|collaborators|activity|billing|deleted-events|kiosk)(?:$|\?|#|\/)/;
// Event-data GETs that expose attendee PII — now auth-protected server-side, so
// the dashboard must send the venue-admin / organizer Bearer token.
const EVENT_ADMIN_READ_RE = /\/api\/events\/[^/]+\/(registrations|waitlist|attendance|rollcall|export\/registrations|returning-visitors|delete-impact)(?:$|\?|#|\/)/;

function decodeJwtPayload(token) {
  try {
    const part = (token || "").split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

function currentPageSlug() {
  try {
    return (window.location.pathname || "").split("/").filter(Boolean)[0] || null;
  } catch (_) {
    return null;
  }
}

function collectVenueAdminTokens() {
  const out = [];
  const seen = new Set();
  try {
    // venueAdminToken_* keys live in localStorage (venuePersist shim) with a
    // sessionStorage fallback for older sessions.
    for (const store of [window.localStorage, window.sessionStorage]) {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.startsWith("venueAdminToken_") && !seen.has(key)) {
          const val = store.getItem(key);
          if (val) {
            seen.add(key);
            out.push({ slug: key.slice("venueAdminToken_".length), token: val });
          }
        }
      }
    }
  } catch (e) {
    console.debug("venue admin token lookup skipped:", e?.message);
  }
  return out;
}

// Pick the venue-admin token that matches THIS request's venue, so a user who
// manages several venues in one browser can't send venue A's token to venue B.
function findVenueAdminToken(url = "") {
  const tokens = collectVenueAdminTokens();
  if (tokens.length === 0) return null;
  if (tokens.length === 1) return tokens[0].token;

  // 1) Exact match on the venue id embedded in the URL: /api/venues/{id}/...
  const urlVenueId = (url.match(/\/api\/venues\/([^/]+)\//) || [])[1] || null;
  if (urlVenueId) {
    const byId = tokens.find((t) => {
      const p = decodeJwtPayload(t.token);
      return p && p.venue_id === urlVenueId;
    });
    if (byId) return byId.token;
  }

  // 2) Event mutations carry no venue id — match the current page's venue slug.
  const slug = currentPageSlug();
  if (slug) {
    const bySlugKey = tokens.find((t) => t.slug === slug);
    if (bySlugKey) return bySlugKey.token;
    const byPayloadSlug = tokens.find((t) => {
      const p = decodeJwtPayload(t.token);
      return p && p.venue_slug === slug;
    });
    if (byPayloadSlug) return byPayloadSlug.token;
  }

  // 3) Nothing matched — fall back to the first available token.
  return tokens[0].token;
}

function findOrganizerToken() {
  try {
    return (
      sessionStorage.getItem("invyt_organizer_token") ||
      localStorage.getItem("invyt_organizer_token") ||
      sessionStorage.getItem("organizer_token") ||
      localStorage.getItem("organizer_token") ||
      null
    );
  } catch (e) {
    console.debug("organizer token lookup skipped:", e?.message);
    return null;
  }
}

export function installEventAuthInterceptor() {
  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || "";
      const method = (config.method || "get").toLowerCase();
      const isMutation = MUTATING_METHODS.has(method) && (EVENT_API_RE.test(url) || VENUE_ADMIN_API_RE.test(url));
      const isAdminRead = VENUE_ADMIN_READ_RE.test(url) || EVENT_ADMIN_READ_RE.test(url);
      if (!isMutation && !isAdminRead) return config;
      // Don't overwrite an explicit Authorization header (e.g. organizer flows already setting one)
      if (config.headers && (config.headers.Authorization || config.headers.authorization)) {
        return config;
      }
      const token = findVenueAdminToken(url) || findOrganizerToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // Never block a request because of interceptor failure
      console.warn("event-auth interceptor error", e);
    }
    return config;
  });

  // Self-heal expired venue-admin sessions: when an authenticated venue-admin
  // API call returns 401 (e.g. after a server SECRET_KEY rotation), clear the
  // stale session and bounce the user to the PIN login instead of leaving them
  // stuck on a dead-end "Invalid or expired venue admin session" error.
  let redirecting = false;
  // Venue-admin API paths that only work with a valid admin token. Excludes
  // /admin/auth (the PIN login itself — a 401 there just means a wrong PIN).
  const VENUE_ADMIN_AUTHED_RE = /\/api\/venues\/[^/]+\/(profile|upload-cover|upload-logo|settings|media|staff|collaborators|billing|admin\/(analytics|export-csv))(\/|\b|$)/;
  // Event mutations (create/update/hero upload etc.) are auth-protected in
  // strict mode; a 401 here on a venue-admin page means the session expired.
  const EVENT_MUTATION_RE = /\/api\/events(\b|\/|$)/;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const method = (error?.config?.method || "get").toLowerCase();
        const isEventMutation =
          ["post", "put", "delete", "patch"].includes(method) && EVENT_MUTATION_RE.test(url);
        const isVenueAdminApi =
          (VENUE_ADMIN_AUTHED_RE.test(url) || VENUE_ADMIN_READ_RE.test(url) || isEventMutation) &&
          !/\/admin\/auth(\/|\b|$)/.test(url);

        if (status === 401 && isVenueAdminApi && !redirecting) {
          const path = window.location.pathname || "";
          const slug = path.split("/").filter(Boolean)[0];
          const onLogin = /\/admin\/login\/?$/.test(path);
          // The kiosk is a trusted, admin-unlocked device — a failed event read
          // there (e.g. expired token) must NOT wipe its unlock or redirect it
          // to the public profile. Let the caller surface the error instead.
          const onKiosk = /\/kiosk\/?$/.test(path);
          const reserved = ["attendee", "u", "e", "discover", "auth", "create-event", "register", "for-venues", "for-organisers", "for-event-goers", "invyt-pro", "pricing", "work", "me"];

          if (onKiosk) {
            return Promise.reject(error);
          }

          // Clear any stale venue-admin session so the UI stops thinking we're
          // logged in. These keys now live in localStorage (venuePersist shim),
          // so clear from BOTH stores or the login page would auto-forward us
          // straight back into the expired session (redirect loop).
          const isStale = (k) =>
            k && (k.startsWith("venueAdminAuth_") || k.startsWith("venueAdminToken_") || k.startsWith("venueAdminRole_"));
          [window.localStorage, window.sessionStorage].forEach((store) => {
            try {
              Object.keys(store).forEach((k) => { if (isStale(k)) store.removeItem(k); });
            } catch (_) { /* storage disabled */ }
          });

          if (slug && !reserved.includes(slug) && !onLogin) {
            redirecting = true;
            toast.error("Your admin session expired. Please sign in again.");
            const next = encodeURIComponent(path + window.location.search);
            setTimeout(() => {
              window.location.assign(`/${slug}/admin/login?next=${next}`);
            }, 600);
          }
        }
      } catch (e) {
        console.warn("event-auth response interceptor error", e);
      }
      return Promise.reject(error);
    }
  );
}
